import { SearchReportResponseData } from '@ltvco/refresh-lib/v1';
import * as yup from 'yup';

export interface OptionType {
  value: string;
  display: string;
  url: string;
}

export interface IUnclaimedSearcParams {
  fn?: string;
  ln?: string;
}

export interface IParsedName {
  firstName: string;
  lastName: string;
}

export type TSearchData =
  | {
      state: string;
      permalink: string;
    }[]
  | SearchReportResponseData;

const removeSpecialChars = (name = '') =>
  name
    .replace(/’/g, "'")
    .replace(/[^A-Za-zÀ-ÖØ-öø-ÿĀ-ž .'-]/g, '')
    .replace(/([.'-])\1+/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

export const schemaUnclaimedMoneySearchForm = yup.object({
  name: yup
    .string()
    .required('Please enter a valid name to complete search.')
    .trim(),
  states: yup
    .array()
    .of(
      yup.object({
        value: yup.string().required(),
        display: yup.string().required(),
        url: yup.string().required(),
      })
    )
    .min(1, 'Please select a state to search'),
});

export const getRedirectData = (
  results: IUnclaimedSearcParams,
  stateInput: OptionType[]
) => {
  const { fn, ln } = results;

  if (fn && ln) {
    const statesSelected = stateInput.map((state) => state.value);

    return {
      fname: fn,
      lname: ln,
      states: statesSelected,
    };
  }

  return {
    fname: undefined,
    lname: undefined,
    states: [],
  };
};

export const getRedirectURL = (
  data: TSearchData,
  params: {
    fname: string;
    lname: string;
    states: string[];
  }
): string | null => {
  const permalink = Array.isArray(data)
    ? data[0]?.permalink
    : data?.report?.permalink;

  if (!permalink) {
    return null;
  }

  const encodedFname = encodeURIComponent(params.fname);
  const encodedLname = encodeURIComponent(params.lname);
  const encodedPermalink = encodeURIComponent(permalink);

  if (params?.states?.length > 1) {
    const encodedStates = params.states
      .map((s) => encodeURIComponent(s))
      .join(',');
    return `/report/unclaimed-money?fname=${encodedFname}&lname=${encodedLname}&multi_state_search=true&states=${encodedStates}&permalink=${encodedPermalink}`;
  }

  const encodedState = encodeURIComponent(params.states[0] ?? '');
  return `/report/unclaimed-money?fname=${encodedFname}&lname=${encodedLname}&state=${encodedState}&permalink=${encodedPermalink}`;
};

export const parseFullName = (input: string): IParsedName | null => {
  const sanitized = removeSpecialChars(input);
  const normalized = sanitized.trim().replace(/\s+/g, ' ');

  if (!normalized) {
    return null;
  }

  if (normalized.includes(',')) {
    const [rawLast, rawFirst = ''] = normalized.split(',');
    const lastName = rawLast.trim().split(' ')[0];
    const firstName = rawFirst.trim().split(' ')[0];

    if (firstName && lastName) {
      return { firstName, lastName };
    }

    return null;
  }

  const tokens = normalized.split(' ');
  if (tokens.length < 2) {
    return null;
  }

  const firstName = tokens[0];
  const lastName = tokens[tokens.length - 1];

  return { firstName, lastName };
};
